import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { UserPlus } from "lucide-react";
import CredentialsPanel from "@/components/CredentialsPanel";

const EMPTY = {
  first_name: "",
  last_name: "",
  distributor_name: "",
  distributor_location: "",
  email: "",
  phone: "",
};

/**
 * AddDistributorUserDialog
 * Used by agency_user and super admin to provision a distributor account
 * directly. After submit, shows the temporary credentials ONCE for the
 * agency user to copy + email manually.
 */
export default function AddDistributorUserDialog({
  open,
  onOpenChange,
  isSuperAdmin,
  userAgencyId,
  agencies = [],
  defaultAgencyId,
  onCreated,
}) {
  const [form, setForm] = useState(EMPTY);
  const [agencyId, setAgencyId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // After-submit state: { user, temp_password }
  const [creds, setCreds] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setCreds(null);
      if (isSuperAdmin) {
        setAgencyId(defaultAgencyId || agencies[0]?.id || "");
      } else {
        setAgencyId(userAgencyId || "");
      }
    }
  }, [open, isSuperAdmin, defaultAgencyId, userAgencyId, agencies]);

  const submit = async (e) => {
    e.preventDefault();
    if (!agencyId) {
      toast.error("Pick an agency");
      return;
    }
    for (const k of [
      "first_name",
      "last_name",
      "distributor_name",
      "distributor_location",
      "email",
      "phone",
    ]) {
      if (!form[k].trim()) {
        toast.error(`${k.replace("_", " ")} is required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/distributor-users", {
        agency_id: agencyId,
        ...form,
      });
      setCreds(data);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err, "Could not create distributor user"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    setCreds(null);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Prevent accidentally closing the credentials panel
        if (!o && creds) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="rounded-sm max-w-lg"
        data-testid="add-distributor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-slate-700" />
            Add distributor user
          </DialogTitle>
          <DialogDescription>
            Manually provision an account. After submit, you'll get a username +
            temp password to email to the distributor.
          </DialogDescription>
        </DialogHeader>

        {creds ? (
          <CredentialsPanel
            user={creds.user}
            tempPassword={creds.temp_password}
            onDone={handleDone}
          />
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {isSuperAdmin && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
                  Agency *
                </Label>
                <Select value={agencyId} onValueChange={setAgencyId}>
                  <SelectTrigger
                    className="mt-1 rounded-sm"
                    data-testid="dist-agency-select"
                  >
                    <SelectValue placeholder="Pick an agency…" />
                  </SelectTrigger>
                  <SelectContent>
                    {agencies
                      .filter((a) => a.active !== false)
                      .map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                          {a.is_default ? " (default)" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field
                label="First name"
                value={form.first_name}
                onChange={(v) => setForm({ ...form, first_name: v })}
                testId="dist-first-name"
              />
              <Field
                label="Last name"
                value={form.last_name}
                onChange={(v) => setForm({ ...form, last_name: v })}
                testId="dist-last-name"
              />
            </div>
            <Field
              label="Distributor name"
              value={form.distributor_name}
              onChange={(v) => setForm({ ...form, distributor_name: v })}
              testId="dist-name"
            />
            <Field
              label="Distributor location"
              value={form.distributor_location}
              onChange={(v) => setForm({ ...form, distributor_location: v })}
              testId="dist-location"
              placeholder="e.g., Charlotte, NC"
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                testId="dist-email"
                type="email"
              />
              <Field
                label="Phone"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                testId="dist-phone"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="dist-submit-btn"
              >
                {submitting ? "Creating…" : "Create account"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testId, type = "text", placeholder }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground font-mono-data">
        {label} *
      </Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 rounded-sm"
        data-testid={testId}
      />
    </div>
  );
}
